import type React from "react";
import { Box, Slider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  DENSITY_LEVELS,
  DENSITY_MAX_INDEX,
  DENSITY_MIN_INDEX,
  densityToIndex,
  indexToDensity,
  type UiDensity,
} from "../../constants/density";
import type translations from "../../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

const densityLabelKeys: Record<UiDensity, TranslationKeys> = {
  ultraCompact: "profile.density.ultraCompact",
  extraCompact: "profile.density.extraCompact",
  compact: "profile.density.compact",
  snug: "profile.density.snug",
  normal: "profile.density.normal",
  comfortable: "profile.density.comfortable",
};

interface DensitySliderProps {
  value: UiDensity;
  onChange: (level: UiDensity) => void;
  disabled?: boolean;
  testId?: string;
  variant?: "sidebar" | "form";
}

const DensitySlider: React.FC<DensitySliderProps> = ({
  value,
  onChange,
  disabled = false,
  testId,
  variant = "form",
}) => {
  const { t } = useTranslation();
  const index = densityToIndex(value);

  const handleChange = (_event: Event, nextIndex: number | number[]) => {
    if (typeof nextIndex === "number") {
      onChange(indexToDensity(nextIndex));
    }
  };

  const marks = DENSITY_LEVELS.map((level, markIndex) => ({
    value: markIndex,
    label: markIndex === densityToIndex("normal") ? "|" : "",
  }));

  return (
    <Box
      data-testid={testId}
      sx={{
        width: variant === "sidebar" ? 180 : "100%",
        minWidth: variant === "form" ? 200 : undefined,
        px: variant === "sidebar" ? 0.5 : 1,
      }}
    >
      <Slider
        value={index}
        min={DENSITY_MIN_INDEX}
        max={DENSITY_MAX_INDEX}
        step={1}
        marks={marks}
        disabled={disabled}
        valueLabelDisplay="off"
        aria-label={t("profile.density.label" as TranslationKeys)}
        aria-valuetext={t(densityLabelKeys[value])}
        onChange={handleChange}
        size="small"
        sx={{
          "& .MuiSlider-mark": {
            width: 4,
            height: 4,
            borderRadius: "50%",
          },
          "& .MuiSlider-markLabel": {
            fontSize: "0.75rem",
            color: "text.secondary",
          },
        }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid={testId ? `${testId}-label` : undefined}
        sx={{ display: "block", textAlign: "center", mt: -0.5 }}
      >
        {t(densityLabelKeys[value])}
      </Typography>
    </Box>
  );
};

export default DensitySlider;
